import { Injectable } from "@angular/core";
import { TraceParent, TraceContext } from "../../interfaces/license/trace-parent.interface";

/**
 * Service for generating W3C Trace Context headers
 * https://www.w3.org/TR/trace-context/
 */
@Injectable({
    providedIn: "root",
})
export class TraceIdService {
    private sessionTraceId: string | null = null;
    private readonly TRACE_KEY = '_kyc_trace_id';

    constructor() {
        // Restore trace ID from sessionStorage (survives page reloads)
        const stored = sessionStorage.getItem(this.TRACE_KEY);
        if (stored) {
            this.sessionTraceId = stored;
        }
    }

    /**
     * Set the trace ID for the current session (from X-Trace-ID header)
     * @param traceId - Trace ID from server response
     */
    public setSessionTraceId(traceId: string | null): void {
        this.sessionTraceId = traceId;
        if (traceId) {
            sessionStorage.setItem(this.TRACE_KEY, traceId);
        } else {
            sessionStorage.removeItem(this.TRACE_KEY);
        }
    }

    /**
     * Get the stored session trace ID
     * @returns Session trace ID or null
     */
    public getSessionTraceId(): string | null {
        return this.sessionTraceId;
    }

    /**
     * Clear the stored session trace ID
     */
    public clearSessionTraceId(): void {
        this.sessionTraceId = null;
        sessionStorage.removeItem(this.TRACE_KEY);
    }

    /**
     * Generate a random hex string of specified length
     * @param length - Number of hex characters to generate
     * @returns Random hex string
     */
    private generateRandomHex(length: number): string {
        const bytes = new Uint8Array(length / 2);
        crypto.getRandomValues(bytes);
        return Array.from(bytes)
            .map((byte) => byte.toString(16).padStart(2, "0"))
            .join("");
    }

    /**
     * Generate a W3C Trace ID (128 bits / 32 hex chars)
     * @returns 32 character hex string
     */
    private generateTraceId(): string {
        return this.generateRandomHex(32);
    }

    /**
     * Generate a W3C Span ID (64 bits / 16 hex chars)
     * @returns 16 character hex string
     */
    private generateSpanId(): string {
        return this.generateRandomHex(16);
    }

    /**
     * Generate a complete TraceParent object
     * @param sampled - Whether the trace should be sampled (default: true)
     * @param traceId - Optional trace ID to use (if not provided, uses session trace ID)
     * @returns TraceParent object or null if no trace ID is available
     */
    public generateTraceParent(sampled: boolean = true, traceId?: string): TraceParent | null {
        // Priority 1: Use provided traceId parameter
        // Priority 2: Use stored session trace ID
        // Do NOT generate random trace ID - only use server-provided trace ID
        const finalTraceId = traceId || this.sessionTraceId;

        // If no trace ID is available, return null
        if (!finalTraceId) {
            return null;
        }

        return {
            version: "00",
            traceId: finalTraceId,
            spanId: this.generateSpanId(),
            flags: sampled ? "01" : "00",
        };
    }

    /**
     * Convert TraceParent object to W3C header string
     * @param traceParent - TraceParent object
     * @returns W3C traceparent header value
     */
    public traceParentToString(traceParent: TraceParent): string {
        return `${traceParent.version}-${traceParent.traceId}-${traceParent.spanId}-${traceParent.flags}`;
    }

    /**
     * Generate a complete TraceContext with traceparent header
     * @param sampled - Whether the trace should be sampled (default: true)
     * @returns TraceContext with traceparent header value, or null if no trace ID is available
     */
    public generateTraceContext(sampled: boolean = true): TraceContext | null {
        const traceParent = this.generateTraceParent(sampled);

        // If no trace parent is available (no session trace ID), return null
        if (!traceParent) {
            return null;
        }

        return {
            traceparent: this.traceParentToString(traceParent),
        };
    }

    /**
     * Parse a W3C traceparent header string into TraceParent object
     * @param traceparent - W3C traceparent header string
     * @returns TraceParent object or null if invalid
     */
    public parseTraceParent(traceparent: string): TraceParent | null {
        // Validate input
        if (!traceparent || typeof traceparent !== 'string') {
            return null;
        }

        const parts = traceparent.split("-");
        if (parts.length !== 4) {
            return null;
        }

        const [version, traceId, spanId, flags] = parts;

        // Validate that all parts exist and are strings
        if (!version || !traceId || !spanId || !flags) {
            return null;
        }

        // Validate format
        if (
            version.length !== 2 ||
            traceId.length !== 32 ||
            spanId.length !== 16 ||
            flags.length !== 2
        ) {
            return null;
        }

        return {
            version,
            traceId,
            spanId,
            flags,
        };
    }

    /**
     * Validate a W3C traceparent header string
     * @param traceparent - W3C traceparent header string
     * @returns true if valid, false otherwise
     */
    public isValidTraceParent(traceparent: string): boolean {
        return this.parseTraceParent(traceparent) !== null;
    }
}
